import CheckboxInput from 'components/common/Forms/CheckboxInput';
import {
  CheckBoxesWrapper,
  DialogFormWrapper,
  FormHeader,
  FormRow,
  FormTitle,
} from 'components/common/Forms/Forms.styled';
import InfoMessage from 'components/common/InfoMessage';
import { CardLoader } from 'components/common/Loading';
import React, { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import { FaCommentDots, FaExclamationTriangle } from 'react-icons/fa';

import { UseTextsPreferencesFormReturn } from './TextsPreferencesHooks';
import { useTextRulingFormKeysValues } from './TextRulingFormHooks';
import {
  SITE_ATTRIBUTE_TEXT_ALLOW_CANCEL,
  SITE_ATTRIBUTE_TEXT_ALLOW_CONFIRMATION,
  SITE_ATTRIBUTE_TEXT_ALLOW_REMINDER,
  SITE_ATTRIBUTE_TEXT_ALLOW_WAITING_LIST,
} from '../../../../configs/siteAndTrustAttributes';
import { useCommonTranslation } from '../../../../hooks/i18n/useCommonTranslation';
import { useTrust } from '../../../../query/trusts';
import { SiteData } from '../../../../types/sites';

interface Props {
  isFetching: boolean;
  site: SiteData;
  attributesForm: UseTextsPreferencesFormReturn;
}

export const TextRulingForm: React.FC<Props> = ({
  isFetching,
  site,
  attributesForm,
}) => {
  const { t } = useCommonTranslation();

  const { attributeRegister, control, handleChange } = attributesForm;
  const watchedValues = useWatch({ control });

  const { data: trust, isLoading: isTrustLoading } = useTrust(site.trust_id);
  const isTrustTextsConfigured = Boolean(trust?.vonage_application_id);

  const keysValues = useTextRulingFormKeysValues({ watchedValues });
  const allOn = Object.values(keysValues).every(Boolean);
  const toggleAllOnOff = useCallback(
    (on: boolean) => {
      Object.keys(keysValues).forEach((key) => handleChange(key, on));
    },
    [handleChange, keysValues],
  );

  return (
    <DialogFormWrapper>
      <FormHeader>
        <FaCommentDots />
        <FormTitle>{t('texting-rules')}</FormTitle>
      </FormHeader>
      {!isTrustLoading && !isTrustTextsConfigured && (
          <InfoMessage
            type="warning"
            style={{
              marginBottom: 'var(--s3)'
            }}
            icon={<FaExclamationTriangle />}
            text={t('texts-not-configured')}
          />
      )}
      <FormRow>
        <CheckBoxesWrapper isVertical>
          <CheckboxInput
            {...attributeRegister(
              SITE_ATTRIBUTE_TEXT_ALLOW_CONFIRMATION,
              (e) => e.target.checked,
            )}
            label={t('allow-sending-appt-confirmation-texts')}
            key={SITE_ATTRIBUTE_TEXT_ALLOW_CONFIRMATION}
            id={SITE_ATTRIBUTE_TEXT_ALLOW_CONFIRMATION}
            checked={keysValues.textsAllowConfirmation}
          />
          <CheckboxInput
            {...attributeRegister(
              SITE_ATTRIBUTE_TEXT_ALLOW_CANCEL,
              (e) => e.target.checked,
            )}
            label={t('allow-sending-appt-cancel-texts')}
            key={SITE_ATTRIBUTE_TEXT_ALLOW_CANCEL}
            id={SITE_ATTRIBUTE_TEXT_ALLOW_CANCEL}
            checked={keysValues.textsAllowCancel}
          />
          <CheckboxInput
            {...attributeRegister(
              SITE_ATTRIBUTE_TEXT_ALLOW_REMINDER,
              (e) => e.target.checked,
            )}
            label={t('allow-sending-appt-reminder-texts')}
            key={SITE_ATTRIBUTE_TEXT_ALLOW_REMINDER}
            id={SITE_ATTRIBUTE_TEXT_ALLOW_REMINDER}
            checked={keysValues.textsAllowReminder}
          />
          <CheckboxInput
            {...attributeRegister(
              SITE_ATTRIBUTE_TEXT_ALLOW_WAITING_LIST,
              (e) => e.target.checked,
            )}
            label={t('allow-sending-waiting-list-texts')}
            key={SITE_ATTRIBUTE_TEXT_ALLOW_WAITING_LIST}
            id={SITE_ATTRIBUTE_TEXT_ALLOW_WAITING_LIST}
            checked={keysValues.textsAllowWaitingList}
          />
        </CheckBoxesWrapper>
      </FormRow>
      <FormRow>
        <CheckboxInput
          name="allOnOff"
          label={t('texts-all-on-off-toggle')}
          isToggle={true}
          onChange={(e) => toggleAllOnOff(e.target.checked)}
          checked={allOn}
        />
      </FormRow>
      {isFetching && <CardLoader fillWrapper={true} />}
    </DialogFormWrapper>
  );
};
